import React, { useEffect, useState, useRef, useCallback } from "react";
import { withRouter } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { selectDirectMailCampaignStats } from 'data/campaigns/selectors';
import { getDirectMailCampaignStats } from 'data/campaigns/actions';

import { selectCampaign } from 'data/campaigns/selectors';
import { isMarketingCampaignOverviewPath } from 'app/routes';

import css from "./style.scss";
import LetterSVG from 'assets/images/letter.svg';
import MailSVG from 'assets/images/mail.svg';
import ClockSVG from 'assets/images/clock.svg';
import CheckSquareSVG from 'assets/images/check-square.svg';
import RefreshSVG from 'assets/images/refresh.svg';
import RightArrow from 'assets/images/arrow-right-white.svg';

// Guards against duplicate mounts of this component (overlapping, non-exclusive route matches
// in app/routes.js can currently render the campaigns page more than once for the same URL —
// see the equivalent guard in ../../DirectMail/index.js). Stopgap: only the first instance to
// mount fetches/renders; any other stays inert for its whole lifetime (it does not retroactively
// activate). A new instance mounted after the active one unmounts can claim the slot instead.
let _activeDirectMailStatsInstance = null;

const DirectMailCampaignStats = ({ history, location }) => {
  const dispatch = useDispatch();

  const currentMarketingCampaign = useSelector(selectCampaign);
  const stats = useSelector(selectDirectMailCampaignStats);
  const campaignId = currentMarketingCampaign?.get("id");
  const isOnCampaignOverview = isMarketingCampaignOverviewPath(location.pathname);

  const [statsItems, setStatsItems] = useState(null);

  const instanceRef = useRef(null);
  if (instanceRef.current === null) {
    instanceRef.current = {};
  }

  const [isActiveInstance] = useState(() => {
    if (_activeDirectMailStatsInstance === null) {
      _activeDirectMailStatsInstance = instanceRef.current;
      return true;
    }

    return false;
  });

  useEffect(() => {
    return () => {
      if (_activeDirectMailStatsInstance === instanceRef.current) {
        _activeDirectMailStatsInstance = null;
      }
    };
  }, []);

  const handleCurrentMarketingCampaignChanged = () => {
    if (campaignId) {
      dispatch(getDirectMailCampaignStats(campaignId));
    }
  }

  // Fetches only when this is the active instance and the user is on (or arrives at) the base
  // Marketing Campaign overview URL, not when navigating within a sub-campaign page (email/direct
  // mail/dialer/etc.) that renders inside this same component without unmounting it.
  useEffect(() => {
    if (!isActiveInstance || !isOnCampaignOverview) return;

    handleCurrentMarketingCampaignChanged();
  }, [campaignId, isOnCampaignOverview, isActiveInstance]);

  useEffect(() => {
    setStatsItems([
      {
        icon: MailSVG,
        metric: stats?.totalPiecesMailed ?? 0,
        descriptorText: "Pieces Mailed"
      },
      {
        icon: ClockSVG,
        metric: stats?.ordersScheduled ?? 0,
        descriptorText: "Scheduled"
      },
      {
        icon: CheckSquareSVG,
        metric: stats?.ordersCompleted ?? 0,
        descriptorText: "Completed"
      },
      {
        icon: RefreshSVG,
        metric: stats?.totalRTS ?? 0,
        descriptorText: "Return to Sender"
      }
    ]);
  }, [stats]);

  const formatNumberWithCommaThousandSeparator = (num) => {
    if (!num || isNaN(num)) return num;
    return Number(num).toLocaleString('en-US');
  }

  if (!isActiveInstance) return null;

  return (
    <>
      {statsItems &&
        <div className={css.directMailStats}>
          <div className={css.header}>
            <div className={css.headerLeftSide}>
              <div className={css.headerIcon}>
                <img src={LetterSVG} />
              </div>

              <div className={css.headerText}>
                Direct Mail
              </div>
            </div>

            <button
              className={css.viewDetailsButton}
              onClick={() => history.push(`/campaign/${currentMarketingCampaign.get("id")}/directmail`)}
            >
              <span className={css.buttonText}>View Details</span>
              <img src={RightArrow} />
            </button>
          </div>

          <div className={css.body}>
            <div className={css.bodyContent}>
              {statsItems?.map((item) =>
                <div className={css.bodyItem}>
                  <div className={css.itemIcon}>
                    <img src={item.icon} />
                  </div>

                  <div className={css.itemContent}>
                    <div className={css.itemMetric}>
                      {formatNumberWithCommaThousandSeparator(item.metric)}
                    </div>
                    <div className={css.itemDescriptor}>
                      {item.descriptorText}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      }
    </>
  );
};

export default withRouter(DirectMailCampaignStats);
